import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Button, Link as MuiLink, Alert, Divider } from '@mui/material';
import GradientCircularProgress from '../components/common/GradientCircularProgress';
import useBackendApi from '../hooks/useBackendApi';
import PrimaryBlueButton from '../components/common/buttons/PrimaryBlueButton';
import { colors } from '../styles/fontStyles';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';

interface ClientBookingData {
  company: string;
  appointment_date: string;
  services_booked: string[];
  qr_code: string;
}

const backendURL = process.env.REACT_APP_BACKEND_URL;

const ClientDownloadEmailTemplatePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [emailData, setEmailData] = useState<ClientBookingData | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const { apiFetchRequest } = useBackendApi();
  const { theme } = useThemeContext();

  useEffect(() => {
    const t = searchParams.get('token');
    if (t) {
      setToken(t);
      fetchClientBookingDetails(t);
    }
  }, [searchParams]);

  const fetchClientBookingDetails = async (token: string) => {
    try {
      const response = await apiFetchRequest({
        endpoint: `${backendURL}/client-booking/${token}`,
        method: 'GET',
      });

      if (response?.status === 200 && response?.body) {
        setEmailData({
          company: response.body.company ?? 'Your company',
          appointment_date: response.body.date,
          services_booked: response.body.services_booked || [],
          qr_code: response.body.qr_code,
        });
      } else {
        setEmailData(null);
      }
    } catch (err) {
      console.error(err);
      setEmailData(null);
    } finally {
      setLoading(false);
    }
  };

  const signupUrl =
    token && backendURL ? `${backendURL.replace(/\/api\/?$/, '')}/signup?token=${encodeURIComponent(token)}` : '';

  const handleCopy = () => {
    if (!emailData) return;
    const text = `Hello,

We have selected FROST to undertake your annual checkups on ${emailData.appointment_date}. This is for the following:

${emailData.services_booked.map((s) => `• ${s}`).join('\n')}

Please click the button below to complete the required forms well in advance of the testing:
${signupUrl}

Sent on behalf of FROST.`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <GradientCircularProgress loadingMessage="Loading details..." />
      </Box>
    );
  }

  if (!emailData) {
    return (
      <Box p={4}>
        <Alert severity="error">Invalid or expired link. Please try again.</Alert>
      </Box>
    );
  }

  return (
    <>
      <Box display="flex" justifyContent="flex-start" mt={4} ml={5}>
        <Box component="img" src="/frost/logo_blue.png" alt="FROST Logo" sx={{ height: 60 }} />
      </Box>
      <Box maxWidth={900} mx="auto" p={3}>
        <Box sx={{ padding: '24px', backgroundColor: colors.white }} display="flex" flexDirection="column" gap="8px">
          <Box sx={{ backgroundColor: colors.white }} display="flex" flexDirection="column">
            <Box>
              <Typography variant="body1" gutterBottom mb={2}>
                We are rolling out a new online system for completion of OSHA forms. This requires all your staff to
                complete a registration form and then fill out the relevant forms.
              </Typography>
              <Typography variant="body1" gutterBottom mb={1}>
                Please copy the following and send it to all your staff who are due to be tested
              </Typography>
            </Box>

            <Box
              onClick={handleCopy}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                fontSize: '14px',
                marginLeft: 'auto',
                mb: 1,
              }}
            >
              <Typography variant="body2" sx={{ mr: 1, color: colors.black }}>
                {copied ? 'Copied!' : 'Click to copy →'}
              </Typography>
              <Box component="img" src="/icons/copy-blue900.svg" alt="copy" sx={{ height: 18 }} />
            </Box>
          </Box>

          <Box sx={{ padding: 4, backgroundColor: colors.gray250 }}>
            <Typography variant="body1" gutterBottom>
              Hello,
            </Typography>
            <Typography variant="body1" gutterBottom>
              We have selected FROST to undertake your annual checkups on {emailData.appointment_date}. This is for the
              following:
            </Typography>

            <Box component="ul" sx={{ pl: 3 }}>
              {emailData.services_booked.map((service, idx) => (
                <li key={idx}>
                  <Typography variant="body1">{service}</Typography>
                </li>
              ))}
            </Box>

            <Typography variant="body1" gutterBottom mt={2}>
              Please click the button below or scan the QR code to complete the required forms well in advance of the
              testing.
            </Typography>

            <Box textAlign="center" my={1}>
              <PrimaryBlueButton buttonText="Complete your forms" onClick={() => window.open(signupUrl, '_blank')} />
            </Box>

            {emailData.qr_code && (
              <Box textAlign="center" mt={2}>
                <Box
                  component="img"
                  src={emailData.qr_code}
                  alt="QR Code to signup"
                  sx={{ width: 200, height: 200, mx: 'auto' }}
                />
              </Box>
            )}

            <Typography variant="body1" mt={3}>
              Sent on behalf of FROST.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Box
              display="flex"
              alignItems="center"
              flexDirection={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center" mb={{ xs: 2, sm: 0 }}>
                <Box
                  component="img"
                  src="/frost/logo_blue.png"
                  alt="FROST Logo"
                  sx={{ height: 94, width: 94, mr: 2 }}
                />
                <Box>
                  <Typography
                    variant="h2"
                    fontWeight="bold"
                    color={theme.custom.pages.frost.clientDownloadEmailTemplatePage.typography.h2.color}
                  >
                    Fast Response
                  </Typography>
                  <Typography
                    variant="h5"
                    color={theme.custom.pages.frost.clientDownloadEmailTemplatePage.typography.h6.color}
                  >
                    OSHA Compliance Simplified
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ClientDownloadEmailTemplatePage;
